import React, { useEffect } from 'react'
import { makeArrayFromLength } from "oolib";
import { useAggrMetricsAcrossDimensions } from '../WidgetBarChart/queries/useAggrMetricsAcrossDimensions';
import { useTagCooccurrenceMatrix } from './queries/useTagCooccurrenceMatrix';
import { HeatMapConfigForm } from './ConfigForm';
import { WIDGET_ELEM_processRawConfigFn } from '../WidgetBarChart/WIDGET_ELEM_processRawConfigFn';
import { WidgetErrorState, WidgetLoadingState } from '../../WidgetRenderer/comps/altStatePlaceholders';
import { useUpdateLoadedWidgetsTracker } from '../../../ReportLandingContext/useUpdateLoadedWidgetsTracker';
import { useReportLandingContext } from '../../../ReportLandingContext';
import { HeatMapGrid } from '@/components/dataviz/HeatMapGrid';
import { filterByHiddenDatapoints, filterChildrenByHiddenDatapoints } from '../utils/filterHiddenDatapoints';
import { readVisibilityStateFromProps, resolveHidden } from '../utils/visibilityState';
import { useAnnoFilterConfig, canDrill } from '../utils/annoDrilldown/useAnnoDrilldown';
import { AnnoDrilldownPanel, AnnoDrilldownCount } from '../utils/annoDrilldown/AnnoDrilldownPanel';

export const Component = ({
  contentType,
  dimensions,
  id,
  widgetIdx,
  activeFilters = [],
  actions,
  presetActiveFilters = [],
  widgetFilters = [],
  style = {},
  dimension1Label,
  dimension2Label,
  enableVisibilityFetching = false,
  prefetchedData,
  useCooccurrence = false,
  cooccurrenceSideA = null,
  cooccurrenceSideB = null,
  cooccurrenceLevel = "tag",
  hatchDiagonal = false,
  // The dimension options as the picker stored them: `filterKey` included,
  // which the processed request-shaped `dimensions` drop.
  dimension1,
  dimension2,
  hideZeroValues = false,
  // Everything else this widget receives from config — the visibility state
  // (mode / dynamicPreset / N / manual list) is read out of here. Must stay
  // last: a rest element can only be the final binding.
  ...visibilityProps
}) => {
  const { updateWidgetDataStore } = useReportLandingContext() || {};

  // Only an annotation heatmap can answer "which annotations sit in this cell",
  // and both axes have to group by a filter the annotation config has.
  const dims = [dimension1, dimension2];
  const filterConfig = useAnnoFilterConfig();
  // Never on a public snapshot: the chart replays frozen data there, and the
  // panel would fire live annotation queries for an anonymous viewer.
  const cellsAreDrillable = !prefetchedData && contentType === "annotations" && canDrill(dims, filterConfig);

  const mergedFilters = [
    ...activeFilters,
    ...presetActiveFilters,
    ...widgetFilters
  ];

  // Default cross-tab path — used for every case EXCEPT two annotation-tag axes.
  const aggr = useAggrMetricsAcrossDimensions({
    activeFilters: mergedFilters,
    vizCardId: id,
    contentType: contentType,
    dimensions: dimensions,
    metrics: [{ "type": "count", "name": "count" }],
    enableVisibilityFetching,
    queryOptions: { enabled: prefetchedData ? false : !useCooccurrence },
    includeZeroCounts: !hideZeroValues,
  })

  // Both axes are annotation-tag dims => they resolve to the same
  // annotations.tags.tags array, which aggrMetricsAcrossDimensions collapses
  // onto the diagonal. Route to the co-occurrence engine, which returns the
  // same hierarchy shape (via reshape), so genViz below is unchanged.
  const cooccurrence = useTagCooccurrenceMatrix({
    activeFilters: mergedFilters,
    contentType: contentType,
    sideA: cooccurrenceSideA,
    sideB: cooccurrenceSideB,
    level: cooccurrenceLevel,
    includeSideUniverses: !hideZeroValues,
    vizCardId: id,
    enabled: !prefetchedData && useCooccurrence,
  })

  const active = useCooccurrence ? cooccurrence : aggr;
  const error = active.error;
  const ref = useCooccurrence ? undefined : aggr.ref;

  const data = prefetchedData || active.data;
  const status = prefetchedData ? "success" : active.status;

  useEffect(() => {
    if (status === "success" && data?.length) {
      updateWidgetDataStore && updateWidgetDataStore(id, data);
    }
  }, [data, status, id, updateWidgetDataStore]);

  useUpdateLoadedWidgetsTracker({status, widgetIdx, hasData: !!data?.length, widgetId: id})

  const genViz = () => {
    // Filter out hidden datapoints
    // Manual mode uses the stored selection; dynamic mode recomputes Top/
    // Bottom-N from the CURRENT data, so it can't go stale after a filter
    // change. This widget has no processRawConfigFn, so config props arrive
    // flat and the state is read from them here.
    const hiddenDim1Datapoints = resolveHidden({
      state: readVisibilityStateFromProps(visibilityProps, 1), data, dimension: 1,
    });
    const hiddenDim2Datapoints = resolveHidden({
      state: readVisibilityStateFromProps(visibilityProps, 2), data, dimension: 2,
    });

    let filteredData = filterByHiddenDatapoints(data, hiddenDim1Datapoints);
    filteredData = filterChildrenByHiddenDatapoints(filteredData, hiddenDim2Datapoints, { keepEmptyGroups: !hideZeroValues });

    const getLengthOfLongestChildrenArray = (data) => {
      let longestSoFar = 0;
      for(const d of data){
        if(d.children?.length > longestSoFar){
          longestSoFar = d.children?.length
        }
      }
      return longestSoFar;
    }

    return (
          <HeatMapGrid
            key={JSON.stringify(activeFilters)}
            data={filteredData}
            valuePath={makeArrayFromLength(getLengthOfLongestChildrenArray(filteredData)).map((d,i) => `children.${i}.count`)}
            tooltipLabelsPath={makeArrayFromLength(getLengthOfLongestChildrenArray(filteredData)).map((d,i) => `children.${i}.display`)}
            labelPath="display"
            colorIdx={widgetIdx % 4}
            style={style}
            xAxisLabel={dimension2Label}
            yAxisLabel={dimension1Label}
            hatchDiagonal={hatchDiagonal}
            renderCellDetails={
              cellsAreDrillable
                ? (cell) => (
                    <AnnoDrilldownPanel
                      datum={cell}
                      dims={dims}
                      baseFilters={mergedFilters}
                      filterConfig={filterConfig}
                    />
                  )
                : undefined
            }
            renderCellSubHeader={
              cellsAreDrillable
                ? (cell) => (
                    <AnnoDrilldownCount
                      datum={cell}
                      dims={dims}
                      baseFilters={mergedFilters}
                      filterConfig={filterConfig}
                    />
                  )
                : undefined
            }
          />
    );
  };

  return (
    <div ref={ref}>
      {status === "loading" ? (
        <WidgetLoadingState/>
      ) : status === "error" ? (
        <WidgetErrorState error={error}/>
      ) : (
        status === "success" && genViz()
      )}
    </div>
  );
};

export const optionDef = { display: "Heat Map Grid", icon: "GridNine" ,value: "heatMapGrid", comp: 'WidgetHeatMapGrid' };

export const defaultConfig = {props: {}} //undefined; // could be some default props in this format: { props: {... } }


export const processRawConfigFn = (_widgetConfig) => {
  const processedConfig = WIDGET_ELEM_processRawConfigFn(_widgetConfig);
  
  const dimension1Label = _widgetConfig.props?.dimension1?.display;
  const dimension2Label = _widgetConfig.props?.dimension2?.display;

  const d1 = _widgetConfig.props?.dimension1;
  const d2 = _widgetConfig.props?.dimension2;

  // Both axes are NON-ROLLUP annotation-tag dims. These always resolve to the
  // single annotations.tags.tags array, which aggrMetricsAcrossDimensions can't
  // cross-tab against itself (the second $unwind is a no-op, so the grid
  // collapses onto the diagonal). Route these to the co-occurrence engine.
  // Covers "tags from subtheme A" × "tags from subtheme B", A × A, and the
  // catch-all "Tags" × anything.
  const isAnnoTagDim = (d) =>
    !!(d && d.isAnno && !d.rollupType &&
       typeof d.valuePath === "string" && d.valuePath === "annotations.tags.tags");

  // Both axes are SUB-THEME ROLLUP dims — the folder-row picks ("Thematics" /
  // "Attributes", restricted via restrictBucketValues) and the bare
  // "All Sub-Themes" rollup. Same single-array collapse one level up: same-role
  // pairs degenerate to the diagonal, cross-role to an empty grid (one element
  // can't satisfy both roles' bucket restrictions). Route these to the
  // co-occurrence engine at level "subTheme" (axes = sub-themes, 1 anno =
  // 1 count per pair). MIXED rollup × non-rollup stays on the aggr path.
  const isSubThemeRollupDim = (d) =>
    !!(d && d.isAnno && d.rollupType === "tagType" &&
       Array.isArray(d.rollupPath) && d.rollupPath.length === 2 &&
       d.rollupPath[1] === "subThemes");

  const bothTagDims = isAnnoTagDim(d1) && isAnnoTagDim(d2);
  const bothSubThemeDims = isSubThemeRollupDim(d1) && isSubThemeRollupDim(d2);
  const useCooccurrence = bothTagDims || bothSubThemeDims;
  const cooccurrenceLevel = bothSubThemeDims ? "subTheme" : "tag";

  // Tag level: a specific-subtheme option ("tags from subtheme A") carries its
  // subThemeId in `value`; the catch-all "Tags" option carries an "ANNO_TAG__…"
  // string and imposes no side constraint (all tags on that axis).
  // SubTheme level: a folder pick carries its role's sub-theme ids in
  // `restrictBucketValues`; the bare "All Sub-Themes" rollup has none — no
  // side constraint (all sub-themes on that axis).
  const toSide = (d) => {
    if (bothSubThemeDims) {
      return Array.isArray(d?.restrictBucketValues) && d.restrictBucketValues.length
        ? { subThemeIds: d.restrictBucketValues }
        : null;
    }
    return d && typeof d.value === "string" && !d.value.startsWith("ANNO_TAG__")
      ? { subThemeIds: [d.value] }
      : null;
  };
  const cooccurrenceSideA = useCooccurrence ? toSide(d1) : null;
  const cooccurrenceSideB = useCooccurrence ? toSide(d2) : null;

  // Same field on both axes: a cell vs itself carries no signal, so the grid
  // hatches the diagonal. Only the co-occurrence path can put the same field
  // on both axes (the aggr path collapses that case onto the diagonal).
  const sideKey = (s) => JSON.stringify([...(s?.subThemeIds || [])].map(String).sort());
  const hatchDiagonal = useCooccurrence && sideKey(cooccurrenceSideA) === sideKey(cooccurrenceSideB);

  return {
    ...processedConfig,
    props: {
      ...processedConfig.props,
      dimension1Label,
      dimension2Label,
      useCooccurrence,
      cooccurrenceSideA,
      cooccurrenceSideB,
      cooccurrenceLevel,
      hatchDiagonal,
    }
  };
};

export const ConfigForm = (props) => <HeatMapConfigForm
  {...props}
  widgetIcon={optionDef.icon}
  widgetTitle={optionDef.display}
  dimensionsInputPropsConfig={{
    input1: {
      label: "Vertical Axis Input"
    },
    input2: {
      label: "Horizontal Axis Input"
    }
  }}
/>;

export const someReqPropsUnset = widgetConfig => {
  if(widgetConfig?.props?.dimension1?.comp === 'GroupQuestionsInputSingle'){
    return false;
  }
  return !widgetConfig?.props?.dimension1 || !widgetConfig?.props?.dimension2
}
